import { useState, useRef, useEffect, useCallback } from "react";

const ANIM_IN  = "tipIn 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards";
const ANIM_OUT = "tipOut 0.18s cubic-bezier(0.4, 0, 1, 1) forwards";

export default function StyledTooltip({
  value,
  label,
  change,
  changeDir = "neutral",
  description,
  children,
}) {
  const [state, setState] = useState("hidden");
  const triggerRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const hideTimer = useRef(null);

  const safeValue =
    isNaN(Number(value)) || value === "" || value == null ? 0 : Number(value);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const TW = 210, TH = 110;
    let top  = rect.top - TH - 14;
    let left = rect.left + rect.width / 2 - TW / 2;
    if (top < 10) top = rect.bottom + 14;
    left = Math.max(10, Math.min(left, window.innerWidth - TW - 10));
    setPos({ top, left });
  }, []);

  useEffect(() => {
    if (state === "hidden") return;
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [state, updatePosition]);

  const show = () => { clearTimeout(hideTimer.current); setState("visible"); };
  const hide = () => {
    setState("hiding");
    hideTimer.current = setTimeout(() => setState("hidden"), 200);
  };

  const colorMap = {
    up:      { value: "#fca5a5", badge: "rgba(252,165,165,0.18)", badgeText: "#fca5a5", badgeBorder: "rgba(252,165,165,0.35)", arrow: "▲" },
    down:    { value: "#6ee7b7", badge: "rgba(110,231,183,0.16)", badgeText: "#6ee7b7", badgeBorder: "rgba(110,231,183,0.35)", arrow: "▼" },
    neutral: { value: "#ffffff", badge: "rgba(255,255,255,0.10)", badgeText: "rgba(255,255,255,0.55)", badgeBorder: "rgba(255,255,255,0.18)", arrow: "" },
  };
  const c = colorMap[changeDir] ?? colorMap.neutral;

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        style={{ cursor: "help", display: "inline-block" }}
      >
        {children}
      </span>

      {state !== "hidden" && (
        <div
          className="lg-tooltip-outer"
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            zIndex: 9999,
            minWidth: 210,
            pointerEvents: "none",
            animation: state === "hiding" ? ANIM_OUT : ANIM_IN,
            filter: "drop-shadow(0 8px 32px rgba(0,0,0,0.5)) drop-shadow(0 2px 6px rgba(0,0,0,0.25))",
          }}
        >
          <div
            className="lg-body"
            style={{
              borderRadius: 18,
              padding: "12px 16px",
              position: "relative",
              overflow: "hidden",
              backdropFilter: "blur(48px) saturate(280%) brightness(1.2) hue-rotate(5deg)",
              WebkitBackdropFilter: "blur(48px) saturate(280%) brightness(1.2) hue-rotate(5deg)",
              background: "linear-gradient(160deg, rgba(20,20,20,0.85) 0%, rgba(20,20,20,0.75) 40%, rgba(20,20,20,0.80) 100%)",
              borderTop:    "1px solid rgba(255,255,255,0.15)",
              borderLeft:   "1px solid rgba(255,255,255,0.12)",
              borderRight:  "1px solid rgba(255,255,255,0.08)",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <div style={{
              position: "absolute", inset: 0, borderRadius: 18, pointerEvents: "none",
              background: "linear-gradient(105deg, rgba(139,92,246,0.12), rgba(6,182,212,0.10), rgba(16,185,129,0.08), rgba(245,158,11,0.09))",
              mixBlendMode: "screen",
            }} />

            <div style={{ position: "relative", zIndex: 1 }}>
              {label && (
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 3 }}>
                  {label}
                </div>
              )}
              <div style={{ fontSize: 19, fontWeight: 700, color: c.value, letterSpacing: "-0.025em", marginBottom: 6, textShadow: "0 1px 8px rgba(0,0,0,0.3)" }}>
                ₹{safeValue.toLocaleString("en-IN")}
              </div>
              {change && (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 3,
                  fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 999,
                  background: c.badge, color: c.badgeText,
                  border: `1px solid ${c.badgeBorder}`,
                  backdropFilter: "blur(10px)",
                }}>
                  {c.arrow} {change}
                </span>
              )}
              {description && (
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.38)", marginTop: 6 }}>
                  {description}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "center", marginTop: -1 }}>
            <div style={{
              width: 0, height: 0,
              borderLeft: "8px solid transparent",
              borderRight: "8px solid transparent",
              borderTop: "9px solid rgba(20,20,20,0.80)",
            }} />
          </div>
        </div>
      )}

      <style>{`
        .lg-body::before {
          content: ""; position: absolute; top: 0; left: 0; right: 0;
          height: 40%; border-radius: 18px 18px 0 0;
          background: linear-gradient(180deg, rgba(255,255,255,0.08), transparent);
          pointer-events: none; z-index: 0;
        }
        .lg-body::after {
          content: ""; position: absolute; top: 1px; left: 8%; width: 40%; height: 2px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,1) 50%, transparent);
          border-radius: 999px; pointer-events: none; z-index: 0;
          animation: lgShimmer 2.8s ease-in-out infinite;
        }
        @keyframes lgShimmer {
          0%   { transform: translateX(-100%); opacity: 0.5; }
          50%  { opacity: 0.9; }
          100% { transform: translateX(200%); opacity: 0.5; }
        }
        @keyframes tipIn  {
          from { opacity: 0; transform: translateY(10px) scale(0.92); filter: blur(6px); }
          to   { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        @keyframes tipOut {
          from { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
          to   { opacity: 0; transform: translateY(6px) scale(0.95); filter: blur(4px); }
        }
      `}</style>
    </>
  );
}
